import {isReadableError} from '@/lib/api/ReadableError'
import {responseData} from '@/lib/api/ResponseData'
import throwIfError from '@/lib/api/throwIfError'
import {getCurrency} from '@/lib/locale/country'
import {
  getLanguageCountryFromLocale,
  localeUnderscoreToHyphen,
  type SupportedLocales,
} from '@/lib/locale/locale'
import {CartCalibratedError} from '@/services/cartService'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UnexpectedError from '@/utils/api/UnexpectedError'
import PermanentError from '@/utils/errors/PermanentError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import tryCatch from '@/utils/tryCatch'
import waitAtLeast from '@/utils/waitAtLeast'

import foodGrubhubCartDb, {
  type FoodGrubhubCartItemWithoutQuantity,
  type FoodGrubhubCartItemWithoutQuantityAndMoreInfo,
  FoodGrubhubCartV1,
  getDefaultFoodGrubhubCart,
} from './foodGrubhubCartDb'
import * as foodGrubhubCartOperations from './foodGrubhubCartOperations'
import {
  FoodGrubhubPaymentResponse,
  GrubhubOrderResponse,
  type GrubhubOrders,
  GrubhubOrdersResponse,
} from './foodGrubhubModel'
// import {FoodGrubhubPaymentResponse, OrderGrubhubResponse} from './foodGrubhubModel'

const NON_ERROR_API_CODES = ['ICA0000']

const FoodGrubhubCartV1Response = responseData(FoodGrubhubCartV1)

export class FoodGrubhubCartExpiredError extends PermanentError {
  version: number

  constructor(version: number) {
    super('Your cart is expired, please start over')
    this.version = version
  }
}

export function isFoodGrubhuhbExpiredError(error: unknown): error is FoodGrubhubCartExpiredError {
  return error instanceof FoodGrubhubCartExpiredError
}

export async function getFoodGrubhubCart({
  locale,
  signal,
  isAuthenticated,
  foodToken,
  longitude,
  latitude,
}: {
  locale: SupportedLocales
  signal?: AbortSignal
} & (
  | {
      isAuthenticated: true
      foodToken?: never
      longitude?: never
      latitude?: never
    }
  | {
      isAuthenticated: false
      locale: SupportedLocales
      signal?: AbortSignal
      foodToken: string
      longitude: number
      latitude: number
    }
)) {
  const {country} = getLanguageCountryFromLocale(locale)

  if (!isAuthenticated) {
    const cart = foodGrubhubCartDb.getFoodGrubhubCart(country)

    return call
      .patch(
        '/api/checkout/v1/gh/food-carts',
        {
          ...cart,
          foodToken,
          longitude,
          latitude,
        },
        {
          signal,
          headers: {
            'Accept-Language': localeUnderscoreToHyphen(locale),
          },
        },
      )
      .then(res => {
        throwIfError(res, NON_ERROR_API_CODES)
        const data = FoodGrubhubCartV1Response(res.data)
        return data.data
      })
      .catch(e => {
        if (shouldLogError(e)) {
          console.error('Cannot get food cart', e)
        }
        throw new UnexpectedError(e)
      })
  }

  return call
    .get('/api/checkout/v1/gh/food-carts', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodGrubhubCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (isReadableError(e) && e.res.data.code === 'ICA1150') {
        console.log(e.res)
        // @ts-expect-error -- our API sucks
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- our API sucks
        throw new FoodGrubhubCartExpiredError(e.res.data.data.ghFoodCart.version)
      }
      if (shouldLogError(e)) {
        console.error('Cannot fetch food cart', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function addToFoodGrubhubCart({
  isAuthenticated,
  item,
  quantity,
  locale,
  signal,
  foodToken,
  longitude,
  latitude,
}: {
  isAuthenticated: boolean
  item: FoodGrubhubCartItemWithoutQuantity
  quantity?: number
  locale: SupportedLocales
  signal?: AbortSignal
  foodToken: string
  longitude: number
  latitude: number
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  let currentFoodCart: FoodGrubhubCartV1

  if (!isAuthenticated) {
    currentFoodCart = foodGrubhubCartDb.getFoodGrubhubCart(country)
  } else {
    const [cart, error] = await tryCatch(getFoodGrubhubCart({locale, isAuthenticated}))
    if (error) {
      currentFoodCart = getDefaultFoodGrubhubCart(
        isFoodGrubhuhbExpiredError(error) ? error.version : 1,
      )
    } else {
      currentFoodCart = cart
    }
  }

  const newFoodCart = foodGrubhubCartOperations.addToFoodGrubhubCart({
    foodGrubhubCart: currentFoodCart,
    item,
    quantity,
  })

  if (!isAuthenticated) {
    return foodGrubhubCartDb.updateFoodGrubhubCart({country, foodCart: newFoodCart})
  }

  return call
    .patch(
      '/api/checkout/v1/gh/food-carts',
      {
        ...newFoodCart,
        foodToken,
        longitude,
        latitude,
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodGrubhubCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot add to food cart', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function removeFromFoodGrubhubCart({
  isAuthenticated,
  itemOrIndex,
  quantity,
  locale,
  signal,
  foodToken,
  longitude,
  latitude,
}: {
  isAuthenticated: boolean
  itemOrIndex: FoodGrubhubCartItemWithoutQuantityAndMoreInfo | number
  quantity?: number // if quantity is 0, <0 or missing, remove the item entirely
  locale: SupportedLocales
  signal?: AbortSignal
  foodToken: string
  longitude: number
  latitude: number
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  let currentFoodCart: FoodGrubhubCartV1

  if (!isAuthenticated) {
    currentFoodCart = foodGrubhubCartDb.getFoodGrubhubCart(country)
  } else {
    const [cart, error] = await tryCatch(getFoodGrubhubCart({locale, isAuthenticated}))
    if (error) {
      currentFoodCart = getDefaultFoodGrubhubCart(
        isFoodGrubhuhbExpiredError(error) ? error.version : 1,
      )
    } else {
      currentFoodCart = cart
    }
  }

  const newFoodCart = foodGrubhubCartOperations.removeFromFoodGrubhubCart({
    foodGrubhubCart: currentFoodCart,
    itemOrIndex,
    quantity,
  })

  if (!isAuthenticated) {
    return waitAtLeast(
      Promise.resolve(foodGrubhubCartDb.updateFoodGrubhubCart({country, foodCart: newFoodCart})),
      300,
    )
  }

  return call
    .patch(
      '/api/checkout/v1/gh/food-carts',
      {
        ...newFoodCart,
        foodToken,
        longitude,
        latitude,
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodGrubhubCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot remove from food cart', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function updateFoodGrubhubCartProductQuantity({
  isAuthenticated,
  itemOrIndex,
  quantity,
  locale,
  signal,
  foodToken,
  longitude,
  latitude,
}: {
  isAuthenticated: boolean
  itemOrIndex: FoodGrubhubCartItemWithoutQuantityAndMoreInfo | number
  quantity: number
  locale: SupportedLocales
  signal?: AbortSignal
  foodToken: string
  longitude: number
  latitude: number
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  let currentFoodCart: FoodGrubhubCartV1

  if (!isAuthenticated) {
    currentFoodCart = foodGrubhubCartDb.getFoodGrubhubCart(country)
  } else {
    const [cart, error] = await tryCatch(getFoodGrubhubCart({locale, isAuthenticated}))
    if (error) {
      currentFoodCart = getDefaultFoodGrubhubCart(
        isFoodGrubhuhbExpiredError(error) ? error.version : 1,
      )
    } else {
      currentFoodCart = cart
    }
  }

  const newFoodCart = foodGrubhubCartOperations.updateFoodGrubhubCartProductQuantity({
    foodGrubhubCart: currentFoodCart,
    itemOrIndex,
    quantity,
  })

  if (!isAuthenticated) {
    return waitAtLeast(
      Promise.resolve(foodGrubhubCartDb.updateFoodGrubhubCart({country, foodCart: newFoodCart})),
      300,
    )
  }

  return call
    .patch(
      '/api/checkout/v1/gh/food-carts',
      {
        ...newFoodCart,
        foodToken,
        longitude,
        latitude,
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodGrubhubCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot update food cart product quantity', e)
      }
      throw new UnexpectedError(e)
    })
}

// There are 2 cases:
// 1. Buy cart for users: items are not provided
// 2. For guests: if items are provided, use them, otherwise use the local cart
export async function createFoodGrubhubPaymentIntent({
  isAuthenticated,
  confirmationTokenId,
  returnUrl,
  signal,
  applyRewards,
  coupon,
  locale,
  email,
  name,
  phone,
  line1,
  line2,
  city,
  state,
  postalCode,
  roundUp,
  driverTip,
  longitude,
  latitude,
  notes,
  foodToken,
  partnerCartId,
  // scheduledId,
}: {
  foodToken: string
  locale: SupportedLocales
  signal?: AbortSignal
  confirmationTokenId: string
  isAuthenticated: boolean
  applyRewards: boolean
  roundUp: boolean
  returnUrl: string
  queryClient: QueryClient
  coupon?: string
  email: string
  name: string
  phone: string
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
  driverTip: number
  longitude: number
  latitude: number
  notes: string
  partnerCartId?: string
  // scheduledId: string | undefined
}) {
  const willUseGuestCart = !isAuthenticated

  const {country} = getLanguageCountryFromLocale(locale)
  const currency = getCurrency(country)?.toLowerCase()

  if (!currency) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  const api = (() => {
    if (isAuthenticated) {
      // Buy cart for users
      return '/api/checkout/v1/gh/payments/payment-intents-for-food-cart'
    }

    // For guests
    return '/api/checkout/pub/v1/gh/payments'
  })()

  const shippingInfo = {
    email,
    name,
    phone,
    line1,
    line2,
    city,
    state,
    postalCode,
    country,
  }

  const commonData = {
    confirmationTokenId,
    shippingInfo,
    checkoutType: 'NO_OP',
    foodToken,
    longitude,
    latitude,
    // scheduledId,
    userDropoffNotes: notes || 'No notes',
    code: coupon,
    roundUp,
    driverTip,
    returnUrl: globalThis.location.origin + returnUrl,
  }

  const data = willUseGuestCart
    ? await (async () => {
        const {v4: uuidV4} = await import('uuid')

        const uuid = uuidV4()
        return {
          ...commonData,
          cartId: uuid,
          email,
          partnerCartId,
          foodItems: foodGrubhubCartDb.getFoodGrubhubCart(country).foodCartItems,
        }
      })()
    : {
        ...commonData,
        useWallet: applyRewards,
      }

  return call
    .post(api, data, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      const data = res.data as object // type-coverage:ignore-line
      if ('code' in data && typeof data.code === 'string' && data.code === 'ICA1120') {
        throw new CartCalibratedError(data)
      }

      throwIfError(res, NON_ERROR_API_CODES)

      const foodPaymentData = FoodGrubhubPaymentResponse(res.data).data

      const order = foodPaymentData.order

      return {
        clientSecret: foodPaymentData.clientSecret,
        order,
        paymentIntentStatus: foodPaymentData.paymentIntentStatus,
      }
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot checkout cart', e)
      }

      if (isReadableError(e)) {
        throw new UserFriendlyError(e.res.data.msg as string, e) // type-coverage:ignore-line
      }
      throw new UnexpectedError(e)
    })
}

// export async function getFoodGrubhubOrderTracking({
//   foodOrderId,
//   signal,
// }: {
//   foodOrderId: string
//   signal?: AbortSignal
// }) {
//   return call
//     .get(`/api/checkout/pub/v1/food-orders/${foodOrderId}/tracking`, {
//       signal,
//     })
//     .then(res => {
//       throwIfError(res, NON_ERROR_API_CODES)
//       const data = OrderGrubhubTrackingResponse(res.data)
//       return data.data
//     })
// }

export async function getFoodOrder({
  foodOrderId,
  signal,
}: {
  foodOrderId: string
  signal?: AbortSignal
}) {
  return call
    .get(`/api/checkout/v1/gh/food-orders/${foodOrderId}`, {
      signal,
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = GrubhubOrderResponse(res.data)
      return data.data
    })
}

export async function confirmFoodOrder({
  foodOrderId,
  signal,
}: {
  foodOrderId: string
  signal?: AbortSignal
}) {
  return call
    .post(`/api/checkout/v1/gh/food-orders/${foodOrderId}/confirm`, {
      signal,
    })
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) {
        throw new Error(msg)
      }
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot confirm food order', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function getFoodOrders({
  signal,
  locale,
}: {
  signal?: AbortSignal
  locale: SupportedLocales
}): Promise<GrubhubOrders> {
  return call
    .get('/api/checkout/v1/gh/food-orders', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      console.log('Raw API response:', res.data)
      const data = GrubhubOrdersResponse(res.data)
      console.log('data', data.data)
      return data.data
    })
}
